import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import FacebookPostInsights from '../../../customer/Integration/FacebookPostInsights';
import InstagramPostInsights from '../../../customer/Integration/InstagramPostInsights';
import MultiCustomerAnalytics from './MultiCustomerAnalytics';
import {
  Users, Search, RefreshCw, ChevronDown, ChevronUp, ExternalLink,
  Facebook, Instagram, Linkedin, Youtube, Globe, CheckCircle,
  AlertCircle, Building2, User, Calendar, Eye, X, ArrowLeft,
  Filter, SlidersHorizontal, Heart, MessageSquare, Share2, Loader2,
  BarChart3, Image as ImageIcon, Play, Pencil, Trash2, Unlink
} from 'lucide-react';

// Platform config for colors and icons
const PLATFORM_CONFIG = {
  facebook: {
    icon: Facebook,
    label: 'Facebook',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    gradient: 'from-blue-500 to-blue-700',
    badge: 'bg-blue-100 text-blue-700',
  },
  instagram: {
    icon: Instagram,
    label: 'Instagram',
    color: 'text-pink-600',
    bg: 'bg-pink-50',
    border: 'border-pink-200',
    gradient: 'from-pink-500 to-purple-600',
    badge: 'bg-pink-100 text-pink-700',
  },
  linkedin: {
    icon: Linkedin,
    label: 'LinkedIn',
    color: 'text-blue-700',
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    gradient: 'from-blue-600 to-blue-800',
    badge: 'bg-sky-100 text-sky-700',
  },
  youtube: {
    icon: Youtube,
    label: 'YouTube',
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    gradient: 'from-red-500 to-red-700',
    badge: 'bg-red-100 text-red-700',
  },
};

const getPlatformConfig = (platform) =>
  PLATFORM_CONFIG[platform] || {
    icon: Globe,
    label: platform || 'Unknown',
    color: 'text-gray-600',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    gradient: 'from-gray-500 to-gray-700',
    badge: 'bg-gray-100 text-gray-700',
  };

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Account detail modal
function AccountDetailModal({ account, customer, onClose, onDisconnect }) {
  if (!account) return null;

  const config = getPlatformConfig(account.platform);
  const PlatformIcon = config.icon;

  // Accordion state
  const [isPostsOpen, setIsPostsOpen] = useState(false);
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(false);

  // Posts state
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [postsError, setPostsError] = useState('');
  const [postsNextCursor, setPostsNextCursor] = useState(null);
  const [loadingMorePosts, setLoadingMorePosts] = useState(false);

  // Post insights modal state
  const [fbInsightsOpen, setFbInsightsOpen] = useState(false);
  const [fbInsightsPost, setFbInsightsPost] = useState(null);
  const [igInsightsOpen, setIgInsightsOpen] = useState(false);
  const [igInsightsPost, setIgInsightsPost] = useState(null);

  // Post edit / delete state
  const [deletingPost, setDeletingPost] = useState(null);
  const [deletePostError, setDeletePostError] = useState(null);
  const [isDeletingPost, setIsDeletingPost] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editPostMessage, setEditPostMessage] = useState('');
  const [isUpdatingPost, setIsUpdatingPost] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // Fetch posts based on platform
  useEffect(() => {
    if (account) fetchPosts();
  }, [account]);

  const fetchPosts = async () => {
    setLoadingPosts(true);
    setPostsError('');
    setPosts([]);
    setPostsNextCursor(null);

    try {
      if (account.platform === 'facebook') {
        await fetchFacebookPosts();
      } else if (account.platform === 'instagram') {
        await fetchInstagramPosts();
      } else if (account.platform === 'linkedin') {
        await fetchLinkedInPosts();
      } else {
        setPostsError('Post viewing is not available for this platform');
      }
    } catch (err) {
      console.error('Error fetching posts:', err);
      setPostsError(err.message || 'Failed to fetch posts');
    } finally {
      setLoadingPosts(false);
    }
  };

  const fetchFacebookPosts = async () => {
    const pages = account.pages || [];
    if (pages.length === 0) {
      setPostsError('No connected Facebook pages found');
      return;
    }

    const allPosts = [];
    const errors = [];

    for (const page of pages) {
      const token = page.accessToken || page.access_token;
      if (!token) {
        errors.push(`Page "${page.name}": no access token stored`);
        continue;
      }
      try {
        // Try /feed first (more permissive), fall back to /posts
        const endpoints = [
          `https://graph.facebook.com/v18.0/${page.id}/feed?fields=id,message,story,created_time,permalink_url,full_picture,attachments{media_type,type},likes.summary(true),comments.summary(true),shares&limit=25&access_token=${token}`,
          `https://graph.facebook.com/v18.0/${page.id}/posts?fields=id,message,created_time,permalink_url,full_picture,type,likes.summary(true),comments.summary(true),shares,reactions.summary(true)&limit=25&access_token=${token}`,
        ];

        let fetched = false;
        for (const url of endpoints) {
          const res = await fetch(url);
          const data = await res.json();
          if (data.error) {
            console.warn(`Graph API error for page "${page.name}":`, data.error);
            errors.push(`${page.name}: ${data.error.message}`);
            continue; // try next endpoint
          }
          if (data.data) {
            // Filter out posts that are just shares (no message/picture from this page)
            const pagePosts = data.data.filter(
              (p) => p.full_picture || p.message || p.story
            );
            allPosts.push(
              ...pagePosts.map((p) => ({
                ...p,
                type: p.attachments?.data?.[0]?.media_type === 'video' ? 'video' : (p.type || 'photo'),
                _pageId: page.id,
                _pageName: page.name,
                _pageAccessToken: token,
                _pageProfilePic: null,
              }))
            );
            fetched = true;
            break;
          }
        }
        if (!fetched && errors.filter((e) => e.startsWith(page.name)).length === 0) {
          errors.push(`${page.name}: no posts returned`);
        }
      } catch (e) {
        console.error(`Error fetching posts for page ${page.id}:`, e);
        errors.push(`${page.name}: ${e.message}`);
      }
    }

    allPosts.sort((a, b) => new Date(b.created_time) - new Date(a.created_time));
    setPosts(allPosts);

    if (allPosts.length === 0 && errors.length > 0) {
      setPostsError(errors.join(' | '));
    }
  };

  const fetchInstagramPosts = async () => {
    // Instagram: fetch media from the IG business account
    const pages = account.pages || [];
    const igAccountId = account.platformUserId;
    let token = null;

    // Find correct access token - page token for IG business accounts
    for (const page of pages) {
      if (page.instagramBusinessAccount || page.instagram_business_account) {
        token = page.accessToken || page.access_token;
        break;
      }
    }
    if (!token) token = account.accessToken;
    if (!token || !igAccountId) {
      setPostsError('No access token or Instagram account ID available');
      return;
    }

    const url = `https://graph.facebook.com/v18.0/${igAccountId}/media?fields=id,caption,media_type,media_product_type,media_url,thumbnail_url,permalink,timestamp,like_count,comments_count,is_comment_enabled&limit=100&access_token=${token}`;
    const res = await fetch(url);
    const data = await res.json();
    if (data.error) {
      setPostsError(data.error.message || 'Instagram API error');
      return;
    }
    setPosts(data.data || []);
    setPostsNextCursor(data.paging?.next ? (data.paging.cursors?.after || null) : null);
  };

  const loadMoreInstagramPosts = async () => {
    if (!postsNextCursor || loadingMorePosts) return;
    const igAccountId = account.platformUserId;
    let token = null;
    for (const page of (account.pages || [])) {
      if (page.instagramBusinessAccount || page.instagram_business_account) {
        token = page.accessToken || page.access_token;
        break;
      }
    }
    if (!token) token = account.accessToken;
    if (!token || !igAccountId) return;
    setLoadingMorePosts(true);
    try {
      const url = `https://graph.facebook.com/v18.0/${igAccountId}/media?fields=id,caption,media_type,media_product_type,media_url,thumbnail_url,permalink,timestamp,like_count,comments_count,is_comment_enabled&limit=100&after=${postsNextCursor}&access_token=${token}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.error) throw new Error(data.error.message || 'Instagram API error');
      setPosts(prev => [...prev, ...(data.data || [])]);
      setPostsNextCursor(data.paging?.next ? (data.paging.cursors?.after || null) : null);
    } catch (err) {
      setPostsError(err.message);
    } finally {
      setLoadingMorePosts(false);
    }
  };

  const fetchLinkedInPosts = async () => {
    const token = account.accessToken;
    if (!token) {
      setPostsError('No LinkedIn access token available');
      return;
    }
    try {
      // Detect organization accounts:
      // 1. platformUserId starts with urn:li:organization: (org account stored directly)
      // 2. organizations[] is non-empty (personal account with linked orgs)
      const isOrgByUrn = account.platformUserId?.includes('urn:li:organization:');
      const isOrgByList = (account.organizations?.length || 0) > 0;
      const isOrg = isOrgByUrn || isOrgByList;

      const organizationId = isOrgByUrn
        ? account.platformUserId
        : (account.organizations?.[0]?.id || '');

      const params = new URLSearchParams({
        token,
        accountType: isOrg ? 'organization' : 'personal',
        organizationId: organizationId,
      });
      const res = await fetch(`${apiUrl}/linkedin/posts?${params}`);
      const data = await res.json();
      if (data.success) {
        setPosts(data.posts || []);
        if ((data.posts || []).length === 0) {
          setPostsError('No posts found for this LinkedIn account');
        }
      } else {
        setPostsError(data.error || data.message || 'Failed to fetch LinkedIn posts');
      }
    } catch (e) {
      setPostsError('Failed to connect to server');
    }
  };

  // Get the correct page token for FB insights
  const getFbPageForPost = (post) => {
    const page = (account.pages || []).find((p) => p.id === post._pageId);
    return page || account.pages?.[0];
  };

  // Handle clicking a post to open insights
  const handlePostClick = (post) => {
    if (account.platform === 'facebook') {
      setFbInsightsPost(post);
      setFbInsightsOpen(true);
    } else if (account.platform === 'instagram') {
      setIgInsightsPost(post);
      setIgInsightsOpen(true);
    }
    // LinkedIn: no dedicated insights modal, detail shown inline
  };

  // Get IG access token for insights
  const getIgAccessToken = () => {
    const pages = account.pages || [];
    for (const page of pages) {
      if (page.instagramBusinessAccount || page.instagram_business_account) {
        return page.accessToken || page.access_token;
      }
    }
    return account.accessToken;
  };

  // Resolve platform token for a given post
  const getPostToken = (post) => {
    if (account.platform === 'facebook') {
      const page = getFbPageForPost(post);
      return page?.accessToken || page?.access_token;
    }
    if (account.platform === 'instagram') return getIgAccessToken();
    return account.accessToken;
  };

  // Delete a post via the platform API
  const handleDeletePost = async (post) => {
    const token = getPostToken(post);
    setIsDeletingPost(true);
    setDeletePostError(null);
    try {
      if (account.platform === 'facebook') {
        const res = await fetch(
          `https://graph.facebook.com/v25.0/${post.id}?access_token=${token}`,
          { method: 'DELETE' }
        );
        const data = await res.json();
        if (data?.success === true) {
          setPosts(prev => prev.filter(p => p.id !== post.id));
          setDeletingPost(null);
        } else {
          setDeletePostError(data?.error?.message || 'Meta restricts API deletion. Open the post directly to delete it.');
        }
      } else if (account.platform === 'instagram') {
        const res = await fetch(
          `https://graph.facebook.com/v18.0/${post.id}?access_token=${token}`,
          { method: 'DELETE' }
        );
        const data = await res.json();
        if (data?.success === true) {
          setPosts(prev => prev.filter(p => p.id !== post.id));
          setDeletingPost(null);
        } else {
          setDeletePostError(data?.error?.message || 'Unable to delete via API. Open the post to delete it.');
        }
      } else if (account.platform === 'linkedin') {
        const res = await fetch(`${apiUrl}/linkedin/post`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, postId: post.id }),
        });
        const data = await res.json();
        if (data?.success) {
          setPosts(prev => prev.filter(p => p.id !== post.id));
          setDeletingPost(null);
        } else {
          setDeletePostError(data?.error || 'Failed to delete via API. Open the post on LinkedIn to delete it manually.');
        }
      }
    } catch {
      setDeletePostError('Network error. Please try again.');
    } finally {
      setIsDeletingPost(false);
    }
  };

  // Update a post's message/caption via the platform API
  const handleUpdatePost = async () => {
    if (!editingPost || !editPostMessage.trim()) return;
    const token = getPostToken(editingPost);
    setIsUpdatingPost(true);
    try {
      if (account.platform === 'facebook') {
        const body = new URLSearchParams({ message: editPostMessage, access_token: token });
        const res = await fetch(`https://graph.facebook.com/v25.0/${editingPost.id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString(),
        });
        const data = await res.json();
        if (!res.ok || data.error) {
          alert(`Failed to update: ${data.error?.message || 'Unknown error'}`);
          return;
        }
        setPosts(prev => prev.map(p => p.id === editingPost.id ? { ...p, message: editPostMessage } : p));
        setEditingPost(null);
      } else {
        alert('Caption editing is not supported by this platform\'s API.');
      }
    } catch {
      alert('Network error while updating post.');
    } finally {
      setIsUpdatingPost(false);
    }
  };

  // Render a single post card
  const renderPostCard = (post, index) => {
    if (account.platform === 'facebook') {
      return (
        <div
          key={post.id || index}
          className="aspect-square relative group cursor-pointer overflow-hidden rounded-lg"
          onClick={() => handlePostClick(post)}
        >
          {post.full_picture ? (
            <img src={post.full_picture} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex flex-col items-center justify-center p-3">
              <Facebook className="h-6 w-6 text-blue-400 mb-2" />
              {post.message && (
                <p className="text-[10px] text-blue-700 line-clamp-4 text-center leading-tight">
                  {post.message}
                </p>
              )}
            </div>
          )}
          {post.type === 'video' && (
            <div className="absolute top-1.5 right-1.5">
              <Play className="w-4 h-4 text-white drop-shadow-lg" fill="white" />
            </div>
          )}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-1.5">
            <div className="flex justify-end gap-1">
              <button
                onClick={(e) => { e.stopPropagation(); setEditPostMessage(post.message || ''); setEditingPost(post); }}
                className="p-1 bg-white/20 hover:bg-white/40 rounded-md transition-colors"
                title="Edit post"
              >
                <Pencil className="h-3 w-3 text-white" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setDeletePostError(null); setDeletingPost(post); }}
                className="p-1 bg-white/20 hover:bg-red-500/60 rounded-md transition-colors"
                title="Delete post"
              >
                <Trash2 className="h-3 w-3 text-white" />
              </button>
            </div>
            <div className="flex items-center justify-center gap-4 text-white text-sm font-semibold">
              <span className="flex items-center gap-1">
                <Heart className="h-4 w-4" fill="white" />
                {post.likes?.summary?.total_count || 0}
              </span>
              <span className="flex items-center gap-1">
                <MessageSquare className="h-4 w-4" fill="white" />
                {post.comments?.summary?.total_count || 0}
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (account.platform === 'instagram') {
      return (
        <div
          key={post.id || index}
          className="aspect-square relative group cursor-pointer overflow-hidden rounded-lg"
          onClick={() => handlePostClick(post)}
        >
          <img
            src={post.thumbnail_url || post.media_url}
            alt=""
            className="w-full h-full object-cover"
          />
          {post.media_type === 'VIDEO' && (
            <div className="absolute top-1.5 right-1.5">
              <Play className="w-4 h-4 text-white drop-shadow-lg" fill="white" />
            </div>
          )}
          {post.media_type === 'CAROUSEL_ALBUM' && (
            <div className="absolute top-1.5 right-1.5">
              <svg className="w-4 h-4 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 24 24">
                <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
              </svg>
            </div>
          )}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-1.5">
            <div className="flex justify-end gap-1">
              <button
                onClick={(e) => { e.stopPropagation(); setDeletePostError(null); setDeletingPost(post); }}
                className="p-1 bg-white/20 hover:bg-red-500/60 rounded-md transition-colors"
                title="Delete post"
              >
                <Trash2 className="h-3 w-3 text-white" />
              </button>
            </div>
            <div className="flex items-center justify-center gap-4 text-white text-sm font-semibold">
              <span className="flex items-center gap-1">
                <Heart className="h-4 w-4" fill="white" />
                {post.like_count || 0}
              </span>
              <span className="flex items-center gap-1">
                <MessageSquare className="h-4 w-4" fill="white" />
                {post.comments_count || 0}
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (account.platform === 'linkedin') {
      return (
        <div
          key={post.id || index}
          className="aspect-square relative group cursor-pointer overflow-hidden rounded-lg"
        >
          {post.hasMedia && post.mediaUrl ? (
            post.mediaType === 'video' ? (
              <div className="relative w-full h-full bg-gray-900">
                <Play className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 text-white drop-shadow-lg z-10" fill="white" />
                <video src={post.mediaUrl} className="w-full h-full object-cover" />
              </div>
            ) : (
              <img src={post.mediaUrl} alt="" className="w-full h-full object-cover" />
            )
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-blue-500 to-indigo-600 p-3 flex flex-col justify-center">
              <Linkedin className="h-5 w-5 text-white mb-2" />
              {post.text && (
                <p className="text-white text-[10px] line-clamp-5 leading-tight">{post.text}</p>
              )}
            </div>
          )}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-1.5">
            <div className="flex justify-end gap-1">
              <button
                onClick={(e) => { e.stopPropagation(); setDeletePostError(null); setDeletingPost(post); }}
                className="p-1 bg-white/20 hover:bg-red-500/60 rounded-md transition-colors"
                title="Delete post"
              >
                <Trash2 className="h-3 w-3 text-white" />
              </button>
            </div>
            <div className="flex items-center justify-center gap-3 text-white text-xs font-semibold">
              {post.likeCount != null && (
                <span className="flex items-center gap-1">
                  <Heart className="h-3 w-3" fill="white" /> {post.likeCount}
                </span>
              )}
              {post.commentCount != null && (
                <span className="flex items-center gap-1">
                  <MessageSquare className="h-3 w-3" fill="white" /> {post.commentCount}
                </span>
              )}
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`bg-gradient-to-r ${config.gradient} p-5 sm:p-6 rounded-t-2xl sticky top-0 z-10`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {account.profilePicture ? (
                  <img src={account.profilePicture} alt="" className="w-12 h-12 rounded-full border-2 border-white/30" />
                ) : (
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <PlatformIcon className="h-6 w-6 text-white" />
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-bold text-white">{account.name || 'Unnamed Account'}</h3>
                  <p className="text-white/70 text-sm">
                    {customer?.customerName} &middot; {config.label}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {onDisconnect && (
                  <button
                    onClick={() => onDisconnect(account, customer)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/80 hover:bg-red-600 rounded-lg text-white text-xs font-medium transition-colors"
                    title="Disconnect this account"
                  >
                    <Unlink className="h-3.5 w-3.5" />
                    Disconnect
                  </button>
                )}
                <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                  <X className="h-5 w-5 text-white" />
                </button>
              </div>
            </div>
          </div>

          {/* Account info summary */}
          <div className="px-5 sm:px-6 py-4 border-b border-gray-100">
            <div className="flex flex-wrap gap-4 text-sm">
              {account.email && (
                <span className="text-gray-500">
                  <span className="font-medium text-gray-700">Email:</span> {account.email}
                </span>
              )}
              <span className="text-gray-500">
                <span className="font-medium text-gray-700">Connected:</span> {formatDate(account.connectedAt || account.createdAt)}
              </span>
              {account.platformUserId && (
                <span className="text-gray-500">
                  <span className="font-medium text-gray-700">ID:</span>{' '}
                  <span className="font-mono text-xs">{account.platformUserId}</span>
                </span>
              )}
              {account.tokenExpiresAt && (
                <span className={new Date(account.tokenExpiresAt) < new Date() ? 'text-red-600' : 'text-gray-500'}>
                  <span className="font-medium text-gray-700">Token:</span>{' '}
                  {new Date(account.tokenExpiresAt) < new Date() ? 'Expired' : `Expires ${formatDate(account.tokenExpiresAt)}`}
                </span>
              )}
            </div>

            {/* Sub-items: Pages / Channels / Orgs */}
            {((account.pages?.length || 0) + (account.channels?.length || 0) + (account.organizations?.length || 0)) > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {account.pages?.map((page, idx) => (
                  <span key={page.id || idx} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                    <Building2 className="h-3 w-3" /> {page.name}
                    {page.fanCount != null && <span className="text-blue-500 ml-1">{Number(page.fanCount).toLocaleString()}</span>}
                  </span>
                ))}
                {account.channels?.map((ch, idx) => (
                  <span key={ch.id || idx} className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium">
                    <Youtube className="h-3 w-3" /> {ch.title || ch.name}
                  </span>
                ))}
                {account.organizations?.map((org, idx) => (
                  <span key={org.id || idx} className="inline-flex items-center gap-1 px-2.5 py-1 bg-sky-50 text-sky-700 rounded-full text-xs font-medium">
                    <Building2 className="h-3 w-3" /> {org.name}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Accordion 1: Posts Feed */}
          <div className="border-b border-gray-100">
            <button
              onClick={() => setIsPostsOpen(!isPostsOpen)}
              className="w-full flex items-center justify-between px-5 sm:px-6 py-4 hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5 text-gray-500" />
                <span className="text-sm font-semibold text-gray-950">Posts Feed</span>
                {posts.length > 0 && (
                  <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    {posts.length}
                  </span>
                )}
              </div>
              <div>
                {isPostsOpen ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </button>

            {isPostsOpen && (
              <div className="px-5 sm:px-6 pb-6 pt-2">
                <div className="flex items-center justify-end mb-4">
                  <button
                    onClick={fetchPosts}
                    disabled={loadingPosts}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`h-3 w-3 ${loadingPosts ? 'animate-spin' : ''}`} />
                    Refresh Feed
                  </button>
                </div>

                {loadingPosts ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 text-gray-400 animate-spin mb-3" />
                    <p className="text-sm text-gray-500">Loading posts from {config.label}...</p>
                  </div>
                ) : postsError ? (
                  <div className="text-center py-8">
                    <AlertCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500 mb-2">{postsError}</p>
                    <button
                      onClick={fetchPosts}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Try Again
                    </button>
                  </div>
                ) : posts.length === 0 ? (
                  <div className="text-center py-8">
                    <ImageIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">No posts found for this account</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-1 sm:gap-1.5">
                      {posts.map((post, idx) => renderPostCard(post, idx))}
                    </div>
                    {account.platform === 'instagram' && postsNextCursor && (
                      <div className="flex items-center justify-center py-4 border-t border-gray-100 mt-2">
                        <button
                          onClick={loadMoreInstagramPosts}
                          disabled={loadingMorePosts}
                          className="flex items-center gap-2 text-sm font-medium text-pink-600 hover:text-pink-700 transition-colors disabled:opacity-50"
                        >
                          {loadingMorePosts
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <ChevronDown className="h-4 w-4" />}
                          {loadingMorePosts ? 'Loading...' : 'Load More Posts'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Accordion 2: Real-time Analytics */}
          <div>
            <button
              onClick={() => setIsAnalyticsOpen(!isAnalyticsOpen)}
              className="w-full flex items-center justify-between px-5 sm:px-6 py-4 hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-gray-500" />
                <span className="text-sm font-semibold text-gray-950">Real-time Analytics</span>
              </div>
              <div>
                {isAnalyticsOpen ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </button>

            {isAnalyticsOpen && (
              <div className="px-5 sm:px-6 pb-6 pt-2 border-t border-gray-100">
                <MultiCustomerAnalytics
                  embedded={true}
                  customerId={customer.customerId}
                  accountId={account._id}
                />
              </div>
            )}
          </div>


        </div>
      </div>

      {/* Facebook Post Insights Modal */}
      {fbInsightsOpen && fbInsightsPost && (() => {
        const page = getFbPageForPost(fbInsightsPost);
        return (
          <FacebookPostInsights
            isOpen={fbInsightsOpen}
            onClose={() => { setFbInsightsOpen(false); setFbInsightsPost(null); }}
            post={fbInsightsPost}
            pageAccessToken={page?.accessToken || page?.access_token}
            pageName={page?.name || account.name}
            pageProfilePic={page?.picture?.data?.url || account.profilePicture}
            onDeletePost={(postId) => setPosts(prev => prev.filter(p => p.id !== postId))}
            onUpdatePost={(postId, newMsg) => setPosts(prev => prev.map(p => p.id === postId ? { ...p, message: newMsg } : p))}
          />
        );
      })()}

      {/* Instagram Post Insights Modal */}
      {igInsightsOpen && igInsightsPost && (
        <InstagramPostInsights
          isOpen={igInsightsOpen}
          onClose={() => { setIgInsightsOpen(false); setIgInsightsPost(null); }}
          post={igInsightsPost}
          accessToken={getIgAccessToken()}
          accountProfile={{
            id: account.platformUserId,
            username: account.name,
            profile_picture_url: account.profilePicture,
          }}
          onDeletePost={(postId) => setPosts(prev => prev.filter(p => p.id !== postId))}
        />
      )}

      {/* Delete confirm modal */}
      {deletingPost && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => { setDeletingPost(null); setDeletePostError(null); }} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-sm font-bold text-gray-900 mb-1">Delete post?</h3>
            {!deletePostError ? (
              <p className="text-xs text-gray-500 mb-4">This will permanently delete the post. This action cannot be undone.</p>
            ) : (
              <>
                <p className="text-xs text-red-600 mb-2">{deletePostError}</p>
                <p className="text-xs text-gray-500 mb-4">
                  Open the post on {account.platform === 'facebook' ? 'Facebook' : account.platform === 'instagram' ? 'Instagram' : 'the platform'} and delete it directly.
                </p>
              </>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => { setDeletingPost(null); setDeletePostError(null); }}
                disabled={isDeletingPost}
                className="flex-1 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              {!deletePostError ? (
                <button
                  onClick={() => handleDeletePost(deletingPost)}
                  disabled={isDeletingPost}
                  className="flex-1 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5"
                >
                  {isDeletingPost ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  {isDeletingPost ? 'Deleting…' : 'Delete'}
                </button>
              ) : (
                <button
                  onClick={() => {
                    const url = deletingPost.permalink_url || deletingPost.permalink || deletingPost.url
                      || (account.platform === 'facebook' ? `https://www.facebook.com/${deletingPost.id}` : `https://www.instagram.com/p/${deletingPost.shortcode || ''}`);
                    window.open(url, '_blank', 'noopener,noreferrer');
                    setDeletingPost(null);
                    setDeletePostError(null);
                  }}
                  className="flex-1 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-1"
                >
                  Open post ↗
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit post modal */}
      {editingPost && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => !isUpdatingPost && setEditingPost(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-sm font-bold text-gray-900 mb-1">Edit post</h3>
            <p className="text-xs text-gray-500 mb-3">
              {account.platform === 'instagram'
                ? 'Instagram does not allow caption editing via API. Open the post on Instagram to edit it.'
                : 'Note: updates only work if the post was created by the same app.'}
            </p>
            {account.platform === 'facebook' ? (
              <>
                <textarea
                  value={editPostMessage}
                  onChange={e => setEditPostMessage(e.target.value)}
                  rows={4}
                  placeholder="Write something…"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => setEditingPost(null)}
                    disabled={isUpdatingPost}
                    className="flex-1 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdatePost}
                    disabled={isUpdatingPost || !editPostMessage.trim()}
                    className="flex-1 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5"
                  >
                    {isUpdatingPost ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pencil className="w-4 h-4" />}
                    {isUpdatingPost ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
              </>
            ) : (
              <div className="flex gap-2 mt-2">
                <button onClick={() => setEditingPost(null)} className="flex-1 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                  Close
                </button>
                <button
                  onClick={() => {
                    const url = editingPost.permalink || editingPost.url || `https://www.instagram.com/p/${editingPost.shortcode || ''}`;
                    window.open(url, '_blank', 'noopener,noreferrer');
                    setEditingPost(null);
                  }}
                  className="flex-1 py-2 rounded-lg bg-pink-600 text-white text-sm font-medium hover:bg-pink-700 transition-colors flex items-center justify-center gap-1"
                >
                  Open on Instagram ↗
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// Platform badge
function PlatformBadge({ platform, count }) {
  const config = getPlatformConfig(platform);
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.badge}`}>
      <Icon className="h-3 w-3" />
      {config.label}
      {count > 1 && <span className="ml-0.5">×{count}</span>}
    </span>
  );
}

// Account card for grid view
function AccountCard({ account, customer, onClick, onDisconnect }) {
  const config = getPlatformConfig(account.platform);
  const Icon = config.icon;

  const subItemCount =
    (account.pages?.length || 0) +
    (account.channels?.length || 0) +
    (account.organizations?.length || 0);

  return (
    <div
      onClick={onClick}
      className={`border ${config.border} rounded-xl p-4 cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 ${config.bg} flex flex-col justify-between h-full`}
    >
      <div>
        <div className="flex items-start gap-3">
          {account.profilePicture ? (
            <img
              src={account.profilePicture}
              alt=""
              className="w-10 h-10 rounded-full border-2 border-white shadow-sm flex-shrink-0"
            />
          ) : (
            <div className={`w-10 h-10 bg-gradient-to-br ${config.gradient} rounded-full flex items-center justify-center flex-shrink-0`}>
              <Icon className="h-5 w-5 text-white" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm truncate">{account.name || 'Unnamed'}</p>
            <p className={`text-xs ${config.color} font-medium`}>{config.label}</p>
            {account.email && <p className="text-xs text-gray-500 truncate mt-0.5">{account.email}</p>}
          </div>
        </div>

        {subItemCount > 0 && (
          <div className="mt-3 text-xs text-gray-500 flex items-center gap-1">
            <Building2 className="h-3 w-3" />
            <span>{subItemCount} {subItemCount === 1 ? 'page' : 'pages'} connected</span>
          </div>
        )}

        {/* Metadata dates */}
        <div className="mt-3 pt-3 border-t border-black/5 space-y-1 text-[11px] text-gray-505">
          <div className="flex justify-between items-center">
            <span className="font-medium text-gray-400">Connected:</span>
            <span className="font-mono text-gray-700">{formatDate(account.connectedAt || account.createdAt)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="font-medium text-gray-400">Token Created:</span>
            <span className="font-mono text-gray-700">{formatDate(account.tokenCreatedAt || account.connectedAt || account.createdAt)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="font-medium text-gray-400">Token Expires:</span>
            <span className={`font-mono ${account.tokenExpiresAt && new Date(account.tokenExpiresAt) < new Date()
              ? 'text-red-500 font-bold'
              : 'text-gray-750'
              }`}>
              {account.tokenExpiresAt
                ? (new Date(account.tokenExpiresAt) < new Date()
                  ? `Expired (${formatDate(account.tokenExpiresAt)})`
                  : formatDate(account.tokenExpiresAt))
                : 'Never'}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-black/5 flex items-center justify-between">
        <span className="text-xs text-gray-450 flex items-center gap-1 hover:text-gray-650">
          <Eye className="h-3 w-3" /> View Details
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onDisconnect && onDisconnect(account, customer); }}
          className="flex items-center gap-1 text-xs text-red-400 hover:text-red-650 hover:bg-red-50 px-2 py-1 rounded-lg transition-colors"
          title="Disconnect account"
        >
          <Unlink className="h-3 w-3" /> Disconnect
        </button>
      </div>
    </div>
  );
}

// Customer row with expandable accounts
function CustomerRow({ customer, isExpanded, onToggle, onSelectAccount, onDisconnectAccount, searchQuery }) {
  const platforms = {};
  customer.socialAccounts.forEach((acc) => {
    platforms[acc.platform] = (platforms[acc.platform] || 0) + 1;
  });

  // Filter accounts by search
  const filteredAccounts = useMemo(() => {
    if (!searchQuery) return customer.socialAccounts;
    const q = searchQuery.toLowerCase();
    return customer.socialAccounts.filter(
      (acc) =>
        (acc.name || '').toLowerCase().includes(q) ||
        (acc.platform || '').toLowerCase().includes(q) ||
        (acc.email || '').toLowerCase().includes(q)
    );
  }, [customer.socialAccounts, searchQuery]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Customer header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 sm:p-5 hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-gradient-to-br from-slate-700 to-slate-900 rounded-full flex items-center justify-center flex-shrink-0">
            <User className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">{customer.customerName}</h3>
            <p className="text-xs text-gray-500 truncate">
              {customer.customerEmail || <span className="italic text-gray-400">No email</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="hidden sm:flex items-center gap-1.5 flex-wrap justify-end">
            {Object.entries(platforms).map(([platform, count]) => (
              <PlatformBadge key={platform} platform={platform} count={count} />
            ))}
          </div>
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full whitespace-nowrap">
            {customer.socialAccounts.length} account{customer.socialAccounts.length !== 1 ? 's' : ''}
          </span>
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Expanded accounts */}
      {isExpanded && (
        <div className="border-t border-gray-100 p-4 sm:p-5 bg-gray-50/50">
          {filteredAccounts.length === 0 ? (
            <p className="text-center text-sm text-gray-500 py-4">No matching accounts found.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredAccounts.map((account) => (
                <AccountCard
                  key={account._id}
                  account={account}
                  customer={customer}
                  onClick={() => onSelectAccount(account, customer)}
                  onDisconnect={onDisconnectAccount}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CustomerSocialAccounts({ embedded = false, customerId = null }) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const abortControllerRef = useRef(null);
  const isFirstLoadRef = useRef(true);

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [expandedCustomers, setExpandedCustomers] = useState(new Set());
  const [expandAll, setExpandAll] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Disconnect state
  const [disconnectTarget, setDisconnectTarget] = useState(null); // { account, customer }
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState('');

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  const fetchData = useCallback(
    async (isRefresh = false) => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();

      if (isRefresh) setIsRefreshing(true);
      else if (isFirstLoadRef.current) {
        setLoading(true);
        isFirstLoadRef.current = false;
      }
      setError('');

      try {
        const adminId = currentUser?._id || currentUser?.id;
        if (!adminId) throw new Error('Admin authentication required');

        // Fetch assigned customers + all social links in parallel
        const [assignedRes, socialRes] = await Promise.all([
          fetch(`${apiUrl}/admin/assigned-customers?adminId=${adminId}`, {
            signal: abortControllerRef.current.signal,
          }),
          fetch(`${apiUrl}/api/admin/customer-social-links`, {
            signal: abortControllerRef.current.signal,
          }),
        ]);

        if (!assignedRes.ok) throw new Error(`Failed to fetch assigned customers: ${assignedRes.status}`);
        if (!socialRes.ok) throw new Error(`HTTP ${socialRes.status}`);

        const [assignedCustomers, socialData] = await Promise.all([
          assignedRes.json(),
          socialRes.json(),
        ]);

        if (!socialData.success) throw new Error(socialData.error || 'Failed to fetch data');

        // Build a Set of assigned customer IDs for fast lookup
        const assignedIds = new Set((assignedCustomers || []).map(c => c._id || c.id));

        // Filter social accounts to only assigned customers
        let filtered = (socialData.data || []).filter(c => assignedIds.has(c.customerId));
        if (customerId) {
          filtered = filtered.filter(c => c.customerId === customerId);
        }
        setCustomers(filtered);
      } catch (err) {
        if (err.name === 'AbortError') return;
        setError(err.message || 'Failed to load social accounts');
      } finally {
        setLoading(false);
        setIsRefreshing(false);
      }
    },
    [apiUrl, currentUser, customerId]
  );

  useEffect(() => {
    fetchData();
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  // Filter customers by search + platform
  const filteredCustomers = useMemo(() => {
    let result = customers;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          (c.customerName || '').toLowerCase().includes(q) ||
          (c.customerEmail || '').toLowerCase().includes(q) ||
          c.socialAccounts.some(
            (acc) =>
              (acc.name || '').toLowerCase().includes(q) ||
              (acc.platform || '').toLowerCase().includes(q) ||
              (acc.email || '').toLowerCase().includes(q)
          )
      );
    }

    if (platformFilter !== 'all') {
      result = result
        .map((c) => ({
          ...c,
          socialAccounts: c.socialAccounts.filter((acc) => acc.platform === platformFilter),
        }))
        .filter((c) => c.socialAccounts.length > 0);
    }

    return result;
  }, [customers, searchQuery, platformFilter]);

  const embeddedAccounts = useMemo(() => {
    if (!embedded) return [];
    return filteredCustomers.flatMap((customer) =>
      (customer.socialAccounts || []).map((account) => ({ account, customer }))
    );
  }, [embedded, filteredCustomers]);

  // Stats
  const stats = useMemo(() => {
    const totalAccounts = customers.reduce((sum, c) => sum + c.socialAccounts.length, 0);
    const platformCounts = {};
    customers.forEach((c) =>
      c.socialAccounts.forEach((acc) => {
        platformCounts[acc.platform] = (platformCounts[acc.platform] || 0) + 1;
      })
    );
    return { totalCustomers: customers.length, totalAccounts, platformCounts };
  }, [customers]);

  const toggleExpand = (customerId) => {
    setExpandedCustomers((prev) => {
      const next = new Set(prev);
      if (next.has(customerId)) next.delete(customerId);
      else next.add(customerId);
      return next;
    });
  };

  const handleExpandAll = () => {
    if (expandAll) {
      setExpandedCustomers(new Set());
    } else {
      setExpandedCustomers(new Set(filteredCustomers.map((c) => c.customerId)));
    }
    setExpandAll(!expandAll);
  };

  const handleSelectAccount = (account, customer) => {
    setSelectedAccount(account);
    setSelectedCustomer(customer);
  };

  const handleDisconnectAccount = (account, customer) => {
    setDisconnectError('');
    setDisconnectTarget({ account, customer });
  };

  const confirmDisconnect = async () => {
    if (!disconnectTarget) return;
    const { account, customer } = disconnectTarget;
    setIsDisconnecting(true);
    setDisconnectError('');
    try {
      const res = await fetch(`${apiUrl}/api/customer-social-links/${encodeURIComponent(account._id)}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to disconnect account');
      // Remove from local state
      setCustomers(prev =>
        prev
          .map(c =>
            c.customerId === customer.customerId
              ? { ...c, socialAccounts: c.socialAccounts.filter(a => a._id !== account._id) }
              : c
          )
          .filter(c => c.socialAccounts.length > 0)
      );
      // Close detail modal if the disconnected account is open
      if (selectedAccount?._id === account._id) {
        setSelectedAccount(null);
        setSelectedCustomer(null);
      }
      setDisconnectTarget(null);
    } catch (err) {
      setDisconnectError(err.message || 'Failed to disconnect');
    } finally {
      setIsDisconnecting(false);
    }
  };

  // Loading state
  if (loading) {
    const loadingSkeleton = (
      <div className="space-y-4">
        <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-48 mb-4" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-100 rounded-xl" />
            ))}
          </div>
        </div>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-200 rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-40" />
                <div className="h-3 bg-gray-200 rounded w-56" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
    return embedded ? <>{loadingSkeleton}</> : <AdminLayout title="Social Accounts">{loadingSkeleton}</AdminLayout>;
  }

  // Error state
  if (error && customers.length === 0) {
    const errorContent = (
      <div className="flex flex-col items-center justify-center h-96 space-y-4">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
          <AlertCircle className="h-8 w-8 text-red-500" />
        </div>
        <p className="text-lg font-medium text-gray-700">Failed to load social accounts</p>
        <p className="text-sm text-gray-500">{error}</p>
        <button
          onClick={() => fetchData()}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium shadow-md hover:shadow-lg transition-all"
        >
          <RefreshCw className="w-4 h-4" />
          Try Again
        </button>
      </div>
    );
    return embedded ? <>{errorContent}</> : <AdminLayout title="Social Accounts">{errorContent}</AdminLayout>;
  }

  const mainContent = (
    <>
      <div className="space-y-4 sm:space-y-6">
        {/* Stats banner */}
        {!embedded && (
          <div className="bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
            </div>
            <div className="relative z-10">
              <h2 className="text-lg sm:text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Globe className="h-5 w-5 sm:h-6 sm:w-6" />
                Customer Social Accounts Overview
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
                <div className="bg-white/10 rounded-xl p-3 border border-white/20">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-blue-300" />
                    <span className="text-blue-100 text-xs">Customers</span>
                  </div>
                  <p className="text-white text-xl font-bold mt-1">{stats.totalCustomers}</p>
                </div>
                <div className="bg-white/10 rounded-xl p-3 border border-white/20">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-green-300" />
                    <span className="text-blue-100 text-xs">Total Accounts</span>
                  </div>
                  <p className="text-white text-xl font-bold mt-1">{stats.totalAccounts}</p>
                </div>
                {Object.entries(stats.platformCounts).map(([platform, count]) => {
                  const config = getPlatformConfig(platform);
                  const Icon = config.icon;
                  return (
                    <div key={platform} className="bg-white/10 rounded-xl p-3 border border-white/20">
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-white/80" />
                        <span className="text-blue-100 text-xs">{config.label}</span>
                      </div>
                      <p className="text-white text-xl font-bold mt-1">{count}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Search + filters */}
        {!embedded && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search customers or accounts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Platform filter */}
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="h-4 w-4 text-gray-400 hidden sm:block" />
                <select
                  value={platformFilter}
                  onChange={(e) => setPlatformFilter(e.target.value)}
                  className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="all">All Platforms</option>
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="youtube">YouTube</option>
                </select>

                {/* Expand / Collapse */}
                {!embedded && (
                  <button
                    onClick={handleExpandAll}
                    className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm hover:bg-gray-100 transition-colors whitespace-nowrap"
                  >
                    {expandAll ? 'Collapse All' : 'Expand All'}
                  </button>
                )}

                {/* Refresh */}
                <button
                  onClick={() => fetchData(true)}
                  disabled={isRefreshing}
                  className="p-2.5 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                  title="Refresh"
                >
                  <RefreshCw className={`h-4 w-4 text-gray-600 ${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {/* Active filters indicator */}
            {(searchQuery || platformFilter !== 'all') && (
              <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                <Filter className="h-3 w-3" />
                <span>
                  Showing {filteredCustomers.length} of {customers.length} customers
                </span>
                {(searchQuery || platformFilter !== 'all') && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setPlatformFilter('all');
                    }}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Customer list */}
        {(embedded ? embeddedAccounts.length === 0 : filteredCustomers.length === 0) ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
            <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              {customers.length === 0 ? 'No Social Accounts Found' : 'No Matching Results'}
            </h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              {customers.length === 0
                ? 'No customers have connected their social media accounts yet. Customers can connect accounts through their portal.'
                : 'Try adjusting your search or filter criteria.'}
            </p>
          </div>
        ) : (
          embedded ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {embeddedAccounts.map(({ account, customer }) => (
                <AccountCard
                  key={`${customer.customerId}-${account._id}`}
                  account={account}
                  customer={customer}
                  onClick={() => handleSelectAccount(account, customer)}
                  onDisconnect={handleDisconnectAccount}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredCustomers.map((customer) => (
                <CustomerRow
                  key={customer.customerId}
                  customer={customer}
                  isExpanded={expandedCustomers.has(customer.customerId)}
                  onToggle={() => toggleExpand(customer.customerId)}
                  onSelectAccount={handleSelectAccount}
                  onDisconnectAccount={handleDisconnectAccount}
                  searchQuery={platformFilter !== 'all' ? '' : searchQuery}
                />
              ))}
            </div>
          )
        )}
      </div>

      {/* Account detail modal */}
      {selectedAccount && (
        <AccountDetailModal
          account={selectedAccount}
          customer={selectedCustomer}
          onClose={() => {
            setSelectedAccount(null);
            setSelectedCustomer(null);
          }}
          onDisconnect={handleDisconnectAccount}
        />
      )}

      {/* Disconnect confirm modal */}
      {disconnectTarget && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => { if (!isDisconnecting) { setDisconnectTarget(null); setDisconnectError(''); } }} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Unlink className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900">Disconnect account?</h3>
                <p className="text-xs text-gray-500">{disconnectTarget.account.name} &middot; {getPlatformConfig(disconnectTarget.account.platform).label}</p>
              </div>
            </div>
            {disconnectError ? (
              <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-4">{disconnectError}</p>
            ) : (
              <p className="text-xs text-gray-500 mb-4">This will permanently remove the connection from the database. The social account itself will not be affected.</p>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => { setDisconnectTarget(null); setDisconnectError(''); }}
                disabled={isDisconnecting}
                className="flex-1 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDisconnect}
                disabled={isDisconnecting}
                className="flex-1 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5"
              >
                {isDisconnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
                {isDisconnecting ? 'Disconnecting…' : 'Disconnect'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
  return embedded ? <>{mainContent}</> : <AdminLayout title="Social Accounts">{mainContent}</AdminLayout>;
}

export default CustomerSocialAccounts;
